import { readonly, ref, type Ref } from 'vue'

/**
 * The operator's theme: light or dark, and nothing else.
 *
 * The first value comes from the OS (`prefers-color-scheme`) when nothing is stored, and
 * from then on the toggle owns it. `tokens.css` already follows the OS when the root has
 * no `data-theme`, so an operator who never touches the toggle gets their system theme —
 * without a third "system" state in the control, which was a choice nobody needed to make.
 *
 * The stored value is also read by the inline script in `index.html`, **before the bundle
 * loads** — that is what stops a dark-mode operator seeing a white flash on every
 * navigation. This module and that script must agree on the key and the two values.
 */
export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'zapio-crm-theme'

const current = ref<Theme>(initialTheme())

function initialTheme(): Theme {
  // `try` because a browser with site data blocked throws on the read itself rather than
  // returning null, and a theme preference is not worth a blank page.
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    // fall through to the OS preference
  }

  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme

  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Applied for this session even if it cannot be remembered.
  }
}

export function useTheme(): { theme: Readonly<Ref<Theme>>; toggle: () => void } {
  function toggle(): void {
    current.value = current.value === 'dark' ? 'light' : 'dark'
    apply(current.value)
  }

  return { theme: readonly(current), toggle }
}
